import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Space, Typography, Spin, message, Select } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { mappingApi } from '../services/api/mapping';

const { Title } = Typography;
const { TextArea } = Input;

interface DocTypeDetail {
  id: string;
  name: string;
  script: string;
  parent?: string;
  dataFields: any[];
  options: any[];
}

const DocTypeEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [docType, setDocType] = useState<DocTypeDetail | null>(null);
  const [docTypeList, setDocTypeList] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      loadDocType();
      loadDocTypeList();
    }
  }, [id]);

  const loadDocType = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await mappingApi.getDocType(id);
      setDocType(response.data);
      form.setFieldsValue({
        name: response.data.name,
        script: response.data.script,
        parent: response.data.parent || undefined,
      });
    } catch (error) {
      console.error('Failed to load DocType:', error);
      message.error('Failed to load DocType');
    } finally {
      setLoading(false);
    }
  };

  const loadDocTypeList = async () => {
    try {
      const response = await mappingApi.listDocTypes();
      setDocTypeList(response.data);
    } catch (error) {
      console.error('Failed to load DocType list:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!id) return;

    setSaving(true);
    try {
      await mappingApi.updateDocType(id, {
        name: values.name,
        script: values.script,
        parent: values.parent || null,
      });
      message.success('DocType updated successfully');
      navigate(`/doctype/${id}`);
    } catch (error) {
      console.error('Failed to update DocType:', error);
      message.error('Failed to update DocType');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!docType) {
    return (
      <div style={{ padding: '24px' }}>
        <Title level={2}>DocType not found</Title>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/doctype/${id}`)}
          >
            Cancel
          </Button>
        </Space>

        <Card>
          <Title level={2}>Edit DocType: {docType.name}</Title>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ maxWidth: '800px' }}
          >
            <Form.Item
              label="ID"
              help="ID cannot be changed"
            >
              <Input value={docType.id} disabled />
            </Form.Item>

            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Please enter a name' }]}
            >
              <Input placeholder="Enter DocType name" />
            </Form.Item>

            <Form.Item
              label="Parent DocType"
              name="parent"
              help="Leave empty for no parent"
            >
              <Select
                placeholder="Select parent DocType"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={docTypeList
                  .filter(dt => dt.id !== id)
                  .map(dt => ({
                    value: dt.id,
                    label: `${dt.name} (${dt.id})`,
                  }))}
              />
            </Form.Item>

            <Form.Item
              label="Script"
              name="script"
              rules={[{ required: true, message: 'Please enter a script' }]}
              help="Groovy script for document processing"
            >
              <TextArea
                rows={20}
                placeholder="Enter Groovy script"
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                >
                  Save Changes
                </Button>
                <Button onClick={() => navigate(`/doctype/${id}`)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Space>
    </div>
  );
};

export default DocTypeEdit;